/**
 * ModalHowReferalWorks.js — модалка «Как это работает» (реферальная программа).
 *
 * Где открывается:
 * - NavBar → кнопка «Пригласить» на ReferralSidebarCard (только desktop)
 * - BillingPage → плитка «Реферальная программа»
 *
 * Телефон (≤540px innerWidth): sheet снизу (высота по контенту, max 92dvh),
 *   ручка сверху, свайп вниз закрывает, без крестика.
 * Планшет и desktop: центрированная карточка с крестиком.
 *
 * Overlay всегда MODAL_Z_INDEX_BASE (3000), чтобы бургер (2000) не висел
 * поверх затемнения.
 */

import { memo, useCallback, useEffect, useRef, useState } from "react";
import ReactModal from "react-modal";
import {
  color_black,
  color_black_ultra_light,
  color_grey_dark,
  color_white,
} from "../../../constants/colors";
import { MODAL_Z_INDEX_BASE } from "../../../constants/layers";
import {
  bottomSheetDragHandleVisualStyle,
  useBottomSheetDrag,
} from "../../../hooks/useBottomSheetDrag";
import { useWindowDimensions } from "../../../utils";
import { ModalRedDefaultButton } from "../../controls/Button/ModalRedDefaultButton";
import { OnestBoldBig, OnestNormalSmall } from "../../styled/TextComponents";
import {
  ReferralCodeBox,
  ReferralImportantNote,
  ReferralRewardCards,
  ReferralSteps,
} from "./ReferralModalParts";
import {
  copyReferralCode,
  getReferralModalDensity,
} from "./referralContent";
import { CloseIcon, GiftOutlineIcon } from "./referralIcons";
import { useI18n } from "../../../i18n/I18nProvider";

const ModalHowReferalWorks = memo(({ isOpen, onClose, referal_code }) => {
  const { t } = useI18n();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);

  const { isPhone, isNarrow, isShort } = getReferralModalDensity(
    screenWidth,
    screenHeight,
  );
  const dense = isNarrow || isShort;
  const modalWidth = isNarrow ? Math.max(screenWidth - 32, 280) : 520;

  const handleClose = useCallback(() => {
    onClose("");
  }, [onClose]);

  const { dragHandleProps, sheetMotionStyle, resetDrag } = useBottomSheetDrag({
    enabled: isPhone,
    onClose: handleClose,
  });

  useEffect(() => {
    if (!isOpen) {
      resetDrag();
    }
  }, [isOpen, resetDrag]);

  const handleCopy = useCallback(() => {
    if (!referal_code) return;
    copyReferralCode(referal_code).then(() => {
      setCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 1600);
    });
  }, [referal_code]);

  const contentStyle = isPhone
    ? {
        backgroundColor: color_white,
        borderRadius: "20px 20px 0 0",
        width: "100%",
        height: "auto",
        maxHeight: "92dvh",
        margin: 0,
        top: "auto",
        left: 0,
        right: 0,
        bottom: 0,
        padding: "0 16px calc(16px + env(safe-area-inset-bottom))",
        boxSizing: "border-box",
        overflow: "hidden",
        border: "none",
        display: "flex",
        flexDirection: "column",
        zIndex: MODAL_Z_INDEX_BASE + 1,
        ...sheetMotionStyle,
      }
    : {
        backgroundColor: color_white,
        borderRadius: dense ? 16 : 20,
        width: modalWidth,
        maxHeight: screenHeight - 16,
        height: "min-content",
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        transform: "translate(-50%, -50%)",
        margin: 0,
        padding: dense ? 16 : 28,
        boxSizing: "border-box",
        overflow: "auto",
        border: "none",
        zIndex: MODAL_Z_INDEX_BASE + 1,
      };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        content: contentStyle,
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.65)",
          backdropFilter: "blur(8px)",
          overflow: "hidden",
          zIndex: MODAL_Z_INDEX_BASE,
        },
      }}
    >
      {isPhone && (
        <div {...dragHandleProps}>
          <div style={bottomSheetDragHandleVisualStyle} />
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: dense ? 10 : 20,
          width: "100%",
          flex: isPhone ? 1 : undefined,
          minHeight: 0,
          overflow: isPhone ? "auto" : undefined,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <GiftOutlineIcon size={dense ? 22 : 28} />
            <OnestBoldBig
              style={{
                fontSize: dense ? 20 : 28,
                lineHeight: 1.15,
                color: color_black,
              }}
            >
              {t("Как это работает")}
            </OnestBoldBig>
          </div>
          {!isPhone && (
            <button
              type="button"
              onClick={handleClose}
              aria-label={t("Закрыть")}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 4,
                lineHeight: 0,
                color: color_grey_dark,
              }}
            >
              <CloseIcon />
            </button>
          )}
        </div>

        <OnestNormalSmall
          style={{
            color: color_black_ultra_light,
            lineHeight: 1.35,
            fontSize: dense ? 12 : 14,
          }}
        >
          {t("Коллега указывает код при регистрации. Бонус — после первой оплаты тарифа, не за пробный период.")}
        </OnestNormalSmall>

        <ReferralSteps isNarrow={isNarrow} isShort={isShort || isPhone} />
        <ReferralCodeBox
          referalCode={referal_code}
          copied={copied}
          onCopy={handleCopy}
          isNarrow={isNarrow}
          isShort={isShort || isPhone}
        />
        <ReferralRewardCards
          isNarrow={isNarrow}
          isShort={isShort || isPhone}
        />
        <ReferralImportantNote isShort={isShort || isPhone} />

        <ModalRedDefaultButton
          onClick={handleClose}
          height={dense ? 36 : 40}
        >
          {t("Понятно")}
        </ModalRedDefaultButton>
      </div>
    </ReactModal>
  );
});

export default ModalHowReferalWorks;
